import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI, dashboardAPI, shipmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { 
  Ship, LogOut, Plus, Bell, LayoutDashboard, Package, Users,
  Search, Filter, ChevronRight, Loader2, Plane, Truck,
  AlertTriangle, FileCheck, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor, getStatusLabel, formatDate, formatCurrency } from '../lib/utils';

function Sidebar({ onLogout }) {
  const location = useLocation();
  
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/dashboard/shipments', icon: Package, label: 'Shipments' },
    { path: '/dashboard/suppliers', icon: Users, label: 'Suppliers' },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 p-6">
      <Link to="/dashboard" className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
          <Ship className="w-5 h-5 text-white" />
        </div>
        <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
      </Link>
      
      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                isActive 
                  ? 'bg-importdocs-primary text-white' 
                  : 'text-importdocs-text-secondary hover:bg-slate-100'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
      
      <Button 
        variant="ghost" 
        className="justify-start text-importdocs-text-secondary hover:text-importdocs-danger"
        onClick={onLogout}
        data-testid="logout-btn"
      >
        <LogOut className="w-5 h-5 mr-2" />
        Logout
      </Button>
    </aside>
  );
}

function NotificationDropdown({ notifications, unreadCount, onMarkAllRead }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <Button 
        variant="ghost" 
        size="icon" 
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notifications-btn"
      >
        <Bell className="w-5 h-5 text-importdocs-text-secondary" />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </Button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50" data-testid="notifications-dropdown">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-importdocs-text">Notifications</h3>
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={onMarkAllRead}>
                  Mark all read
                </Button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="p-4 text-center text-importdocs-text-muted">No notifications</p>
              ) : (
                notifications.slice(0, 10).map((n) => (
                  <div 
                    key={n.id} 
                    className={`p-4 border-b border-slate-100 ${!n.is_read ? 'bg-importdocs-subtle' : ''}`}
                  >
                    <p className="text-sm text-importdocs-text">{n.message}</p>
                    <p className="text-xs text-importdocs-text-muted mt-1">
                      {formatDate(n.created_at)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DashboardHeader({ user, notifications, unreadCount, onMarkAllRead, onLogout }) {
  return (
    <header className="bg-white border-b border-slate-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="lg:hidden flex items-center gap-2">
          <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
            <Ship className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
        </div>
        <div className="hidden lg:block">
          <h1 className="font-heading font-bold text-xl text-importdocs-text">
            Welcome back{user?.company_name ? `, ${user.company_name}` : ''}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <NotificationDropdown 
            notifications={notifications} 
            unreadCount={unreadCount}
            onMarkAllRead={onMarkAllRead}
          />
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={onLogout}
          >
            <LogOut className="w-5 h-5 text-importdocs-text-secondary" />
          </Button>
        </div>
      </div>
    </header>
  );
}

function StatsCards({ stats }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <Card className="border-slate-200" data-testid="stat-total">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-importdocs-primary/10 flex items-center justify-center">
              <Package className="w-6 h-6 text-importdocs-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-importdocs-text">{stats.total || 0}</p>
              <p className="text-sm text-importdocs-text-muted">Total Shipments</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-slate-200" data-testid="stat-pending">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-importdocs-text">{stats.by_status?.pending || 0}</p>
              <p className="text-sm text-importdocs-text-muted">Pending</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-slate-200" data-testid="stat-cleared">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
              <FileCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-importdocs-text">{stats.by_status?.cleared || 0}</p>
              <p className="text-sm text-importdocs-text-muted">Cleared</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-slate-200" data-testid="stat-variances">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-importdocs-text">{stats.cost_variances || 0}</p>
              <p className="text-sm text-importdocs-text-muted">Cost Variances</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ShipmentCard({ shipment }) {
  const TransportIcon = shipment.transport_type === 'Sea' ? Ship : 
                        shipment.transport_type === 'Air' ? Plane : Truck;
  
  return (
    <Link to={`/dashboard/shipments/${shipment.id}`}>
      <Card className="feature-card border-slate-200 hover:border-importdocs-primary/30" data-testid={`shipment-card-${shipment.id}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-importdocs-subtle flex items-center justify-center">
                <TransportIcon className="w-5 h-5 text-importdocs-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-importdocs-text">{shipment.name}</h3>
                <p className="text-sm font-mono text-importdocs-text-muted">{shipment.reference}</p>
              </div>
            </div>
            <Badge className={`${getStatusColor(shipment.status)} border`}>
              {getStatusLabel(shipment.status)}
            </Badge>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-importdocs-text-secondary">Supplier</span>
              <span className="text-importdocs-text">{shipment.supplier_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-importdocs-text-secondary">Origin</span>
              <span className="text-importdocs-text">{shipment.country_of_origin}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-importdocs-text-secondary">ETA</span>
              <span className="text-importdocs-text">{formatDate(shipment.estimated_arrival)}</span>
            </div>
            
            <div className="pt-3 border-t border-slate-100">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-importdocs-text-secondary">Documents</span>
                <span className="text-importdocs-text font-medium">{shipment.document_completion}%</span>
              </div>
              <Progress value={shipment.document_completion} className="h-2" />
            </div>
          </div>
          
          <div className="mt-4 flex items-center text-sm text-importdocs-primary font-medium">
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [shipments, setShipments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, shipmentsRes, notifRes, unreadRes] = await Promise.all([
        dashboardAPI.getStats(),
        shipmentsAPI.getAll(),
        notificationsAPI.getAll(),
        notificationsAPI.getUnreadCount(),
      ]);
      setStats(statsRes.data);
      setShipments(shipmentsRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const filteredShipments = shipments.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-importdocs-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-importdocs-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-importdocs-bg flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col">
        <DashboardHeader 
          user={user}
          notifications={notifications}
          unreadCount={unreadCount}
          onMarkAllRead={handleMarkAllRead}
          onLogout={handleLogout}
        />
        
        <main className="flex-1 p-6">
          <StatsCards stats={stats} />
          
          <Card className="border-slate-200" data-testid="shipments-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Recent Shipments</CardTitle>
              <Link to="/dashboard/shipments/new">
                <Button className="bg-importdocs-primary hover:bg-importdocs-primary-dark" data-testid="new-shipment-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Shipment
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-importdocs-text-muted" />
                  <Input 
                    placeholder="Search shipments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-slate-200"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40 border-slate-200" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="documents_uploaded">Docs Uploaded</SelectItem>
                    <SelectItem value="cleared">Cleared</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {filteredShipments.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-importdocs-text-muted mx-auto mb-4" />
                  <p className="text-importdocs-text-secondary">No shipments found</p>
                  <Link to="/dashboard/shipments/new">
                    <Button variant="outline" className="mt-4" data-testid="empty-new-shipment-btn">
                      Create your first shipment
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredShipments.slice(0, 6).map((shipment) => (
                    <ShipmentCard key={shipment.id} shipment={shipment} />
                  ))}
                </div>
              )}
              
              {filteredShipments.length > 6 && (
                <div className="mt-6 text-center">
                  <Link to="/dashboard/shipments">
                    <Button variant="outline" data-testid="view-all-btn">
                      View All Shipments
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
